<template>
  <div :class="formVariants({ theme })">
    <div>
      <div class="flex flex-col gap-4 lg:flex-row">
        <Input
          :label="t('company-postal-address.name')"
          v-model="modelCompany.companyName"
          :style-input="inputVariants({ theme })"
          style-error="relative"
          :v$="v$?.companyName"
          class="mb-7"
          required
        />
      </div>
      <div class="flex flex-col gap-4 lg:flex-row">
        <Input
          :label="t('company-postal-address.address')"
          v-model="modelCompany.address.street"
          :style-input="inputVariants({ theme })"
          style-error="!relative"
          :v$="v$?.address?.street"
          class="mb-7 grow-[2]"
          required
        />
        <Input
          :label="t('company-postal-address.postcode')"
          v-model="modelCompany.address.postcode"
          :style-input="inputVariants({ theme })"
          style-error="!relative"
          :v$="v$?.address?.postcode"
          class="mb-7"
          required
        />
      </div>
      <Input
        :label="t('company-postal-address.city')"
        v-model="modelCompany.address.city"
        :style-input="inputVariants({ theme })"
        style-error="!relative"
        :v$="v$?.address?.city"
        class="mb-7"
        required
      />
      <MultiSelect
        :title="t('company-postal-address.select-country')"
        style-input="lg:w-8/12"
        :options="countries"
        :required="true"
        v-model="modelCompany.address.countryCode"
      />
    </div>
    <Button
      :title="props.nextText || t('button.next')"
      color="pink"
      class="mt-8 w-full lg:mt-10 lg:w-[315px]"
      @click="handleClickNext"
      :arrow-right="true"
    />
  </div>
</template>

<script setup lang="ts">
import { Input, Button, MultiSelect, THEMES } from 'refresh-ui'
import { cva } from 'class-variance-authority'
import { useVModel } from '@vueuse/core'
import type { Validation } from '@vuelidate/core'
import type { Company } from '../../types/company'
import { useI18n } from 'vue-i18n'

import { useCountries } from '../../composables/useCountries'
import type { SiteLang } from '../../types/env'

export interface ICompanyPostalValidation {
  companyName: string
  address: {
    street: string
    postcode: string
    countryCode: string
    city: string
  }
}

const { t } = useI18n()
const route = useRoute()
const config = useRuntimeConfig()

const lang = (route.query.lang as SiteLang) || config.public.SITE_LANG || 'sv'

const { countries } = useCountries(lang)

const props = withDefaults(
  defineProps<{
    company: Company
    v$: Validation<ICompanyPostalValidation>
    theme?: THEMES
    nextText?: string
  }>(),
  {
    theme: THEMES.DEFAULT,
    nextText: '',
  },
)

const emits = defineEmits(['update:company', 'onNext'])

const modelCompany = useVModel(props, 'company', emits)

const handleClickNext = () => {
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()
    if (!props.v$.$invalid) emits('onNext')
    return
  }
  emits('onNext')
}

const inputVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]:
        'border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full',
      [THEMES.RDK]: 'border border-rdk-orange focus:border-rdk-orange w-full',
      [THEMES.DJURENS_RATT]:
        'border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const formVariants = cva('xl:max-w-[575px] w-full', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]: 'text-purple-dark [&_label]:text-purple-dark font-avenir',
      [THEMES.DJURENS_RATT]: '',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
